import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  pairwise,
  Subscription,
  switchMap,
} from 'rxjs';

import { ResponsiveService } from 'src/app/services/responsive.service';
import { Brand, Model, PartnerType } from '../../../models/brand.interface';
import { Environment } from '../../../models/environment.type';
import { Interactivity } from '../../../models/interactivity.enum';
import { MachineType } from '../../../models/machine-type.interface';
import { CompatibilityService } from '../../../services/compatibility.service';
import { TranslateModule } from '@ngx-translate/core';
import { CollapsibleDirective } from '../../../../../../../directive/collapsible.directive';
import { MachinesFilterPanelComponent } from './machines-filter-panel/machines-filter-panel.component';
import { NgClass, AsyncPipe, UpperCasePipe } from '@angular/common';
import { LazyImageDirective } from '../../../../../../../directive/lazyImage.directive';
import { CompatibilityCardComponent } from '../../shared/compatibility-card/compatibility-card.component';

@Component({
  selector: 'app-compatible-machines',
  templateUrl: './compatible-machines.component.html',
  styleUrls: ['./compatible-machines.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CompatibilityCardComponent,
    LazyImageDirective,
    MachinesFilterPanelComponent,
    NgClass,
    CollapsibleDirective,
    AsyncPipe,
    UpperCasePipe,
    TranslateModule,
  ],
})
export class CompatibleMachinesComponent implements OnDestroy {
  private compatibilityService: CompatibilityService =
    inject(CompatibilityService);
  private responsiveService: ResponsiveService = inject(ResponsiveService);

  private readonly _subscriptions: Subscription[] = [];
  readonly interactivities: {
    id: Interactivity;
    name: string;
    tooltip: string;
  }[] = [
    {
      id: Interactivity.Interactive,
      name: 'compatibility.compatibleMachines.interactivities.interactive.name',
      tooltip:
        'compatibility.compatibleMachines.interactivities.interactive.tooltip',
    },
    {
      id: Interactivity.Smart,
      name: 'compatibility.compatibleMachines.interactivities.smart.name',
      tooltip: 'compatibility.compatibleMachines.interactivities.smart.tooltip',
    },
    {
      id: Interactivity.Classic,
      name: 'compatibility.compatibleMachines.interactivities.classic.name',
      tooltip:
        'compatibility.compatibleMachines.interactivities.classic.tooltip',
    },
  ];
  readonly brand$: Observable<Brand | null> =
    this.compatibilityService.selectedBrand$;
  private readonly _filteredModels$: BehaviorSubject<Model[]> =
    new BehaviorSubject<Model[]>([]);
  readonly isSmallScreen$: Observable<boolean> =
    this.responsiveService.width$.pipe(
      map((width: number) => {
        return width <= 1000;
      })
    );
  readonly machineTypes$: Observable<MachineType[]> =
    this._filteredModels$.pipe(
      map((models: Model[]) => {
        return this.compatibilityService.machineTypes.filter(
          (machineType: MachineType) =>
            models
              .map((model: Model) => model.machineType.id)
              .includes(machineType.id)
        );
      })
    );
  readonly isEnvironmentSelected$: Observable<boolean> =
    this.compatibilityService.selectedEnvironment$.pipe(
      map((env: Environment) => {
        return env !== 'all';
      })
    );
  readonly selectedEnvironment$: Observable<Environment> =
    this.compatibilityService.selectedEnvironment$;
  readonly isNoEquipmentSelected$: Observable<boolean> =
    this.compatibilityService.selectedEquipment$.pipe(
      map((equipment: MachineType) => {
        return !equipment;
      })
    );
  readonly PartnerType: typeof PartnerType = PartnerType;

  getModelsByInteractivityAndMachineTypeId$(
    interactivity: Interactivity,
    machineTypeId: number
  ): Observable<Model[]> {
    return this._filteredModels$.pipe(
      map((models: Model[]) => {
        return models.filter((model: Model) => {
          return (
            model.interactivity === interactivity &&
            model.machineType.id === machineTypeId
          );
        });
      })
    );
  }

  onApplyFilters(models: Model[]): void {
    this._filteredModels$.next(models);
  }

  private resetFilteredModels(brand: Brand | null): Model[] {
    return brand ? brand.models : [];
  }

  ngOnInit(): void {
    this._subscriptions.push(
      this.isSmallScreen$
        .pipe(
          pairwise(),
          filter(([previous, current]: [boolean, boolean]) => {
            return !previous && current;
          }),
          switchMap(() => {
            return this.brand$;
          })
        )
        .subscribe((brand: Brand) => {
          this._filteredModels$.next(this.resetFilteredModels(brand));
        }),
      this.brand$.subscribe((brand: Brand) => {
        this._filteredModels$.next(this.resetFilteredModels(brand));
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
